import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import {
  DISCOUNT_STATE_STYLES,
  KIND_LABELS,
  PLAN_SCOPE_LABELS,
  discountLabel,
  discountState,
  usesLabel,
} from '@/components/admin/DiscountForm';
import { DISCOUNT_KINDS, DISCOUNT_PLANS } from '@/shared/constants';
import { DISCOUNT_FIELDS } from '@/shared/fields';
import { idOf, paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/discounts', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'TicketPercent', label: 'Discount codes', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 45 };

const PAGE_SIZE = 20;
const SWITCH = ['On', 'Off'];

export default function AdminDiscounts() {
  const navigate = useNavigate();
  const [active, setActive] = useState('all');
  const [kind, setKind] = useState('all');
  const [plan, setPlan] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [active, kind, plan, q]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('discount_codes_c').select(DISCOUNT_FIELDS);
    if (active !== 'all') query = query.where(eq('active_c', active === 'On'));
    if (kind !== 'all') query = query.where(eq('kind_c', kind));
    if (plan !== 'all') query = query.where(eq('plan_c', plan));
    if (q) query = query.where(contains('Name', q));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [active, kind, plan, q, page]);

  const list = data?.rows ?? [];
  const filtered = active !== 'all' || kind !== 'all' || plan !== 'all' || !!q;

  function clear() {
    setSearch('');
    setActive('all');
    setKind('all');
    setPlan('all');
  }

  const columns = [
    {
      label: 'Code',
      render: (d) => (
        <RowLink to={`/admin/discounts/${d.Id}`} mono sub={discountLabel(d)}>
          {d.Name}
        </RowLink>
      ),
    },
    { label: 'Uses', className: 'hidden sm:table-cell tabular-nums', render: (d) => usesLabel(d) },
    {
      label: 'Expires',
      className: 'hidden md:table-cell text-muted-foreground',
      render: (d) => (d.expires_at_c ? formatLocalDate(d.expires_at_c, DATE_FORMATS.SHORT) : 'Never'),
    },
    {
      label: 'Referrer',
      className: 'hidden lg:table-cell',
      render: (d) =>
        idOf(d.customer_c) ? (
          <RowLink to={`/admin/customers/${idOf(d.customer_c)}`}>{d.customer_c?.Name ?? 'View customer'}</RowLink>
        ) : (
          <Dash />
        ),
    },
    { label: 'State', render: (d) => <StatusBadge value={discountState(d)} styles={DISCOUNT_STATE_STYLES} /> },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Discount codes"
        description="Promos, referral codes and the personal win-back codes reminders hand out."
        actions={
          <Button asChild>
            <Link to="/admin/discounts/new">
              <ApperIcon name="Plus" size={16} />
              New code
            </Link>
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search codes…" />
        <FilterSelect value={active} onChange={setActive} values={SWITCH} labels={{ On: 'Switched on', Off: 'Switched off' }} allLabel="On and off" />
        <FilterSelect value={kind} onChange={setKind} values={DISCOUNT_KINDS} labels={KIND_LABELS} allLabel="All kinds" />
        <FilterSelect value={plan} onChange={setPlan} values={DISCOUNT_PLANS} labels={PLAN_SCOPE_LABELS} allLabel="Any plan scope" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'TicketPercent',
          title: filtered ? 'No codes match' : 'No discount codes yet',
          description: filtered ? 'Nothing fits these filters.' : 'Make a promo for the next big match night.',
          action: filtered ? clear : () => navigate('/admin/discounts/new'),
          actionLabel: filtered ? 'Clear filters' : 'New code',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>
    </div>
  );
}
